import {
  DEFAULT_ALERT_AI_PROMPT,
  ALERT_AI_PROMPT_MAX_LENGTH,
} from '../../../api/src/alerts/alerts-ai.defaults';
import { useState, type FormEvent, type ReactNode } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
} from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import type { TelegramChannel } from '../api/types';
import {
  ALERT_PRIORITIES,
  ALERT_PRIORITY_LABELS,
  YANDEX_SEVERITIES,
  type AlertAiInfo,
  type AlertSettings,
} from '../../../api/src/alerts/alerts.types';
import type { SearchCatalogProperty } from '../../../api/src/search-metrics/search-metrics.types';
const severityNames = {
  FATAL: 'Фатальные',
  CRITICAL: 'Критичные',
  POSSIBLE_PROBLEM: 'Возможные проблемы',
  RECOMMENDATION: 'Рекомендации',
};
export function AlertsSettings({
  workspace,
  settings,
  channels,
  properties,
  editable,
  busy,
  yandexStatus,
  aiInfo,
  onSave,
}: {
  workspace: string;
  settings: AlertSettings;
  channels: TelegramChannel[];
  properties: SearchCatalogProperty[];
  editable: boolean;
  busy: boolean;
  yandexStatus: ReactNode;
  aiInfo: AlertAiInfo | null;
  onSave: (settings: AlertSettings) => void;
}) {
  const [draft, setDraft] = useState(settings);
  const yandex = (patch: Partial<AlertSettings['yandex']>) =>
    setDraft({ ...draft, yandex: { ...draft.yandex, ...patch } });
  return (
    <Stack
      component="form"
      gap={3}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSave({
          ...draft,
          sites: draft.sites.map((s) => s.trim()).filter(Boolean),
          senders: draft.senders.map((s) => s.trim()).filter(Boolean),
          yandex: {
            ...draft.yandex,
            senders: draft.yandex.senders.map((s) => s.trim()).filter(Boolean),
          },
          otherSenders: draft.otherSenders.map((s) => s.trim()).filter(Boolean),
        });
      }}
    >
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2}>
          <Typography variant="h6">Доставка в Telegram</Typography>
          <FormControlLabel
            control={
              <Switch
                disabled={!editable || busy}
                checked={draft.enabled}
                onChange={(_, enabled) => setDraft({ ...draft, enabled })}
              />
            }
            label="Отправлять выбранные алерты"
          />
          <TextField
            select
            disabled={!editable || busy}
            label="Подключение Telegram"
            value={draft.channelId ?? ''}
            onChange={(e) => setDraft({ ...draft, channelId: e.target.value || null })}
          >
            <MenuItem value="">Не выбрано</MenuItem>
            {channels.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
                {!c.enabled ? ' · выключено' : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            disabled={!editable || busy}
            type="number"
            label="ID темы в группе, если используется"
            value={draft.topicId ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, topicId: e.target.value ? Number(e.target.value) : null })
            }
          />
          <Typography variant="body2" color="text.secondary">
            Бот и чат настраиваются в настройках отчётов «Карты выдачи» или «Доноров». После
            изменения настроек ещё не отправленные уведомления отменяются.
          </Typography>
        </Stack>
      </Paper>
      <Accordion
        variant="outlined"
        disableGutters
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          type="button"
          id="alerts-yandex-settings-header"
          aria-controls="alerts-yandex-settings-content"
          expandIcon={<ExpandMore />}
          sx={{ px: 3, py: 1 }}
        >
          <Stack component="span" gap={0.5}>
            <Typography component="span" variant="h6">
              Яндекс Вебмастер
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              {draft.yandex.enabled ? 'Алерты включены' : 'Алерты выключены'} ·{' '}
              {draft.yandex.source === 'api'
                ? `API диагностики · сайтов: ${draft.yandex.propertyIds.length}`
                : 'Письма через Gmail и n8n'}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
          <Stack gap={2}>
            <FormControlLabel
              control={
                <Switch
                  disabled={!editable || busy}
                  checked={draft.yandex.enabled}
                  onChange={(_, enabled) => yandex({ enabled })}
                />
              }
              label="Получать алерты Яндекса"
            />
            <TextField
              select
              disabled={!editable || busy}
              label="Источник алертов Яндекса"
              value={draft.yandex.source}
              onChange={(e) => yandex({ source: e.target.value as 'api' | 'mail' })}
              helperText="Выбран один источник. В режиме почты API диагностики не опрашивается."
            >
              <MenuItem value="api">API диагностики</MenuItem>
              <MenuItem value="mail">Письма через Gmail и n8n</MenuItem>
            </TextField>
            {draft.yandex.source === 'mail' ? (
              <>
                <TextField
                  disabled={!editable || busy}
                  multiline
                  minRows={2}
                  label="Адреса отправителей Яндекса, по одному в строке"
                  value={draft.yandex.senders.join('\n')}
                  onChange={(e) => yandex({ senders: e.target.value.split('\n') })}
                  helperText="Точные email из поля «От» писем Вебмастера, без имени и масок (*). Можно оставить пустым: письма Яндекса попадут в «Требует разбора» без Telegram, даже если их домен указан ниже."
                />
                <Alert severity="info">
                  Включите уведомления по почте в Яндекс Вебмастере на тот же Gmail-ящик. Ниже
                  добавьте домены и настройте AI-промпт. Пока отправитель и сайт не настроены,
                  письма требуют разбора. При сбое Вебмастера письма тоже могут задерживаться.
                </Alert>
              </>
            ) : (
              <>
                <TextField
                  disabled={!editable || busy}
                  type="number"
                  label="Интервал проверки, минут (15–1440)"
                  value={draft.yandex.intervalMinutes}
                  onChange={(e) => yandex({ intervalMinutes: Number(e.target.value) })}
                />
                <Typography fontWeight={700}>Сайты</Typography>
                <Typography variant="body2" color="text.secondary">
                  Выберите сайты для проверки через API. Списки отправителей и доменов для писем
                  ниже на эти проверки не влияют.
                </Typography>
                {!properties.length && (
                  <Alert severity="info">
                    Подключите Яндекс в разделе «Метрики → Подключения» и загрузите каталог сайтов.
                  </Alert>
                )}
                {properties.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        disabled={!editable || busy}
                        checked={draft.yandex.propertyIds.includes(p.id)}
                        onChange={(_, checked) =>
                          yandex({
                            propertyIds: checked
                              ? [...draft.yandex.propertyIds, p.id]
                              : draft.yandex.propertyIds.filter((id) => id !== p.id),
                          })
                        }
                      />
                    }
                    label={`${p.label}${!p.connectionIds.length ? ' · нет активного подключения' : ''}`}
                  />
                ))}
                <Typography fontWeight={700}>Категории для Telegram</Typography>
                <Stack direction="row" flexWrap="wrap">
                  {YANDEX_SEVERITIES.map((s) => (
                    <FormControlLabel
                      key={s}
                      control={
                        <Checkbox
                          disabled={!editable || busy}
                          checked={draft.yandex.severities.includes(s)}
                          onChange={(_, checked) =>
                            yandex({
                              severities: checked
                                ? [...draft.yandex.severities, s]
                                : draft.yandex.severities.filter((v) => v !== s),
                            })
                          }
                        />
                      }
                      label={severityNames[s]}
                    />
                  ))}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={!editable || busy}
                      checked={draft.yandex.notifyResolved}
                      onChange={(_, notifyResolved) => yandex({ notifyResolved })}
                    />
                  }
                  label="Уведомлять об устранении выбранных проблем"
                />
                <Alert severity="info">
                  Первая проверка сохраняет текущие проблемы и отправляет выбранные категории, если
                  доставка включена. Следующие проверки уведомляют только об изменениях. Недостаток
                  данных показывается отдельно.
                </Alert>
                {yandexStatus}
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2}>
          <Typography variant="h6">Письма · Gmail через n8n</Typography>
          <TextField
            label="URL приёма писем из n8n"
            value={`${window.location.origin}/api/workspaces/${workspace}/alerts/mail`}
            slotProps={{ input: { readOnly: true } }}
            helperText="POST с API-токеном редактора в заголовке Authorization: Bearer TOKEN."
          />
          <TextField
            disabled={!editable || busy}
            multiline
            minRows={2}
            label="Адреса отправителей GSC, по одному в строке"
            value={draft.senders.join('\n')}
            onChange={(e) => setDraft({ ...draft, senders: e.target.value.split('\n') })}
            helperText="Точные email из поля «От» писем GSC, без имени и масок (*). Можно оставить пустым: письма GSC попадут в «Требует разбора» без Telegram, даже если их домен указан ниже."
          />
          <TextField
            disabled={!editable || busy}
            multiline
            minRows={3}
            label="Домены для писем, по одному в строке"
            value={draft.sites.join('\n')}
            onChange={(e) => setDraft({ ...draft, sites: e.target.value.split('\n') })}
            helperText="Общий список сайтов для всех почтовых источников. Пусто — письма требуют разбора, без Telegram. Домен учитывается только вместе с настроенным отправителем. В теме или тексте должен встретиться ровно один домен из списка; www и другие поддомены добавьте отдельно. На API Яндекса поле не влияет."
          />
          <TextField
            disabled={!editable || busy}
            multiline
            minRows={2}
            label="Адреса других отправителей, по одному в строке"
            value={draft.otherSenders.join('\n')}
            onChange={(e) => setDraft({ ...draft, otherSenders: e.target.value.split('\n') })}
            helperText="Хостинг, мониторинги, юридические уведомления. Только точные email; не дублируйте адреса GSC и Яндекса. Добавьте их также в Gmail-фильтр n8n."
          />
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2}>
          <Typography variant="h6">AI-анализ писем</Typography>
          <Alert severity={aiInfo?.configured ? 'info' : 'warning'}>
            {aiInfo?.configured
              ? `Ключ AI настроен. Тема и полный текст подходящего письма передаются в OpenAI.`
              : 'AI недоступен: добавьте OPENAI_API_KEY на сервере. Письма будут сохраняться для ручного разбора.'}
          </Alert>
          <TextField
            select
            disabled={!editable || busy}
            label="Модель AI для писем"
            value={draft.ai.model}
            onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, model: e.target.value } })}
            helperText={
              aiInfo?.models.find((model) => model.id === draft.ai.model)?.description ??
              'Модель не поддерживается. Выберите вариант из списка.'
            }
          >
            {!aiInfo?.models.some((model) => model.id === draft.ai.model) && (
              <MenuItem value={draft.ai.model} disabled>
                {draft.ai.model} · не поддерживается
              </MenuItem>
            )}
            {aiInfo?.models.map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.label} · {model.id}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Модель сохраняется для текущего workspace. Сохраните настройки и проверьте письмо:
            доступ по ключу, скорость и результат могут различаться между моделями.
          </Typography>
          <TextField
            required
            disabled={!editable || busy}
            multiline
            minRows={12}
            maxRows={16}
            label="Промпт анализа писем"
            value={draft.ai.prompt}
            onChange={(e) => setDraft({ ...draft, ai: { ...draft.ai, prompt: e.target.value } })}
            slotProps={{ htmlInput: { maxLength: ALERT_AI_PROMPT_MAX_LENGTH } }}
            helperText={`${draft.ai.prompt.length} / ${ALERT_AI_PROMPT_MAX_LENGTH}. Сохраните промпт, затем откройте «Проверить письмо». Текст письма подставляется автоматически.`}
          />
          {editable && (
            <Button
              disabled={busy}
              onClick={() =>
                setDraft({ ...draft, ai: { ...draft.ai, prompt: DEFAULT_ALERT_AI_PROMPT } })
              }
            >
              Вернуть исходный промпт
            </Button>
          )}
          <Typography fontWeight={700}>Приоритеты для Telegram</Typography>
          <Stack direction="row" flexWrap="wrap">
            {ALERT_PRIORITIES.map((priority) => (
              <FormControlLabel
                key={priority}
                label={ALERT_PRIORITY_LABELS[priority]}
                control={
                  <Checkbox
                    disabled={!editable || busy}
                    checked={draft.ai.notifyPriorities.includes(priority)}
                    onChange={(_, checked) =>
                      setDraft({
                        ...draft,
                        ai: {
                          ...draft.ai,
                          notifyPriorities: checked
                            ? [...draft.ai.notifyPriorities, priority]
                            : draft.ai.notifyPriorities.filter((p) => p !== priority),
                        },
                      })
                    }
                  />
                }
              />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Telegram получает только письма выбранного приоритета, требующие действий. Остальные
            остаются в журнале без отправки. Неопределённый результат и ошибки AI попадают в
            «Требует разбора». Диагностика API Яндекса настраивается отдельно.
          </Typography>
        </Stack>
      </Paper>
      {editable && (
        <Button disabled={busy} type="submit" variant="contained" size="large">
          Сохранить настройки
        </Button>
      )}
      <Typography variant="body2" color="text.secondary">
        Подключение n8n и ограничения описаны в «Документация → Алерты поисковых систем».
      </Typography>
    </Stack>
  );
}
